import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  MedicineBoxOutlined,
  MinusCircleOutlined,
  QuestionCircleOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { ATTENDANCE_STATUS_LABELS } from '@sportninja/common/features/game/attendance/constants'
import type { ComponentType } from 'react'
import type { PlayerRosterStatus } from '../types'

export interface RosterPillConfig {
  label: string
  Icon: ComponentType
  color: string
}

type PillStatusKind = Exclude<PlayerRosterStatus['kind'], 'no_response'>

/**
 * Label, icon and fill for each Status pill. Colours and wording match the
 * mobile roster pills (AttendanceSubtitle / GoalieSubtitle).
 */
export const ROSTER_STATUS_PILLS: Record<PillStatusKind, RosterPillConfig> = {
  starting_goalie: {
    label: 'Starting Goalie',
    Icon: InfoCircleOutlined,
    color: colors.INFO_300,
  },
  suspended: {
    label: 'Suspended',
    Icon: ExclamationCircleOutlined,
    color: colors.ERROR_300,
  },
  injured: {
    label: 'Injured',
    Icon: MedicineBoxOutlined,
    color: colors.ERROR_300,
  },
  playing: {
    label: 'Playing',
    Icon: CheckCircleOutlined,
    color: colors.SUCCESS_300,
  },
  away: {
    label: 'Away',
    Icon: MinusCircleOutlined,
    color: colors.NEUTRAL_100,
  },
  available: {
    label: ATTENDANCE_STATUS_LABELS.roster.attending,
    Icon: CheckCircleOutlined,
    color: colors.SUCCESS_300,
  },
  maybe: {
    label: ATTENDANCE_STATUS_LABELS.roster.maybe,
    Icon: QuestionCircleOutlined,
    color: colors.WARNING_300,
  },
  not_available: {
    label: ATTENDANCE_STATUS_LABELS.roster.notAttending,
    Icon: CloseOutlined,
    color: colors.ERROR_300,
  },
}

/**
 * Spares grid overrides (SN-8476): spares are asked whether they're available,
 * so their answer reads Available / Not Available instead of Attending.
 */
export const SPARE_STATUS_PILLS: Partial<
  Record<PillStatusKind, RosterPillConfig>
> = {
  available: {
    ...ROSTER_STATUS_PILLS.available,
    label: ATTENDANCE_STATUS_LABELS.spare.attending,
  },
  not_available: {
    ...ROSTER_STATUS_PILLS.not_available,
    label: ATTENDANCE_STATUS_LABELS.spare.notAttending,
  },
}

/** Soccer overrides: a soccer goalie is a keeper. */
export const SOCCER_STATUS_PILLS: Partial<
  Record<PillStatusKind, RosterPillConfig>
> = {
  starting_goalie: {
    ...ROSTER_STATUS_PILLS.starting_goalie,
    label: 'Starting Keeper',
  },
}

/** A spare's per-game confirmation by a team official (SN-8476). */
export const SPARE_CONFIRMED_PILL: RosterPillConfig = {
  label: 'Confirmed',
  Icon: SafetyCertificateOutlined,
  color: colors.INFO_300,
}

export const SPARE_NOT_CONFIRMED_PILL: RosterPillConfig = {
  label: 'Not Confirmed',
  Icon: ClockCircleOutlined,
  color: colors.NEUTRAL_100,
}
